import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ReactNode } from 'react'

interface SortableListProps<T> {
  items: T[]
  /** Stable identity per item; must not change as the item is edited */
  getItemId: (item: T) => string
  onReorder: (items: T[]) => void
  renderItem: (item: T, index: number) => ReactNode
}

/**
 * Vertical drag-to-reorder list. Owns the dnd wiring so consumers only handle the reordered result,
 * and pairs with `SortableListItem` for the row chrome.
 */
export const SortableList = <T,>({
  items,
  getItemId,
  onReorder,
  renderItem,
}: SortableListProps<T>) => {
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = items.findIndex((item) => getItemId(item) === active.id)
    const newIndex = items.findIndex((item) => getItemId(item) === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    onReorder(arrayMove(items, oldIndex, newIndex))
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(getItemId)} strategy={verticalListSortingStrategy}>
        {items.map(renderItem)}
      </SortableContext>
    </DndContext>
  )
}
